// Evaluations Page - List and create evaluations
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import Layout from '../components/Layout';
import { StatusBadge, ScoreBar } from '../components/StatusBadge';
import { evaluationsApi, eventsApi, usersApi } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { el, enUS } from 'date-fns/locale';
import { 
  ClipboardCheck, 
  Plus, 
  Calendar,
  User,
  ChevronRight,
  FileDown,
  Trash2,
  Search,
  Flag
} from 'lucide-react';

export default function EvaluationsPage() {
  const { user, isAdmin } = useAuth();
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [evaluations, setEvaluations] = useState([]);
  const [allEvaluations, setAllEvaluations] = useState([]);
  const [myEvaluations, setMyEvaluations] = useState([]);
  const [givenEvaluations, setGivenEvaluations] = useState([]);
  const [events, setEvents] = useState({});
  const [users, setUsers] = useState({});
  const [criteria, setCriteria] = useState({});
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('all_time');
  const [activeTab, setActiveTab] = useState(isAdmin ? 'all' : 'received');
  const [searchName, setSearchName] = useState('');

  useEffect(() => {
    loadData();
  }, [period]);

  const loadData = async () => {
    setLoading(true);
    try {
      const promises = [
        evaluationsApi.getMy({ period }),
        evaluationsApi.getMy({ period, as_evaluator: true }),
        eventsApi.getAll(),
        usersApi.getAll(),
        evaluationsApi.getCriteria()
      ];
      if (isAdmin) promises.push(evaluationsApi.getAll({ period }));
      
      const results = await Promise.all(promises);
      
      setMyEvaluations(results[0].data);
      setGivenEvaluations(results[1].data.filter(e => e.evaluation_type !== 'self'));
      
      const eventMap = {};
      results[2].data.forEach(e => eventMap[e.event_id] = e);
      setEvents(eventMap);
      
      const userMap = {};
      results[3].data.forEach(u => userMap[u.user_id] = u);
      setUsers(userMap);

      setCriteria(results[4].data || {});
      
      if (isAdmin && results[5]) {
        setAllEvaluations(results[5].data);
      }
    } catch (error) {
      console.error('Error loading evaluations:', error);
    } finally {
      setLoading(false);
    }
  };

  const timePeriods = [
    { value: 'this_month', label: t('thisMonth') },
    { value: 'quarter', label: t('quarter') },
    { value: 'semester', label: t('semester') },
    { value: 'year', label: t('year') },
    { value: 'two_years', label: t('twoYears') },
    { value: 'all_time', label: t('allTime') },
  ];

  const getEvaluationTypeLabel = (type) => {
    switch(type) {
      case 'self': return t('selfEvaluation');
      case 'peer': return t('peerEvaluation');
      case 'supervisor': return t('supervisorEvaluation');
      default: return type;
    }
  };

  // Build a map: criterion_key -> localized label
  const criterionLabelMap = useMemo(() => {
    const map = {};
    Object.values(criteria || {}).forEach(cat => {
      (cat.criteria || []).forEach(c => {
        map[c.key] = language === 'el' ? c.el : c.en;
      });
    });
    return map;
  }, [criteria, language]);

  // For each evaluation_id, compute the list of low-score (1-2) flags with escalating
  // levels (yellow=1st occurrence, orange=2nd, red=3rd+) per (evaluatee, criterion)
  // counted chronologically across ALL evaluations the current user can see.
  const flagsByEvalId = useMemo(() => {
    const pool = [
      ...(allEvaluations || []),
      ...(myEvaluations || []),
      ...(givenEvaluations || [])
    ];
    // Dedupe by evaluation_id
    const seen = new Set();
    const unique = [];
    for (const e of pool) {
      if (!seen.has(e.evaluation_id)) {
        seen.add(e.evaluation_id);
        unique.push(e);
      }
    }
    // Sort oldest -> newest
    unique.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));

    const counters = {}; // { `${evaluatee_id}::${criterion_key}`: count }
    const result = {};   // { evaluation_id: [{ key, label, level }] }

    for (const ev of unique) {
      const flags = [];
      for (const s of (ev.scores || [])) {
        if (s.score >= 1 && s.score <= 2) {
          const key = `${ev.evaluatee_id}::${s.criterion_key}`;
          counters[key] = (counters[key] || 0) + 1;
          const count = counters[key];
          const level = count === 1 ? 'yellow' : count === 2 ? 'orange' : 'red';
          flags.push({
            key: s.criterion_key,
            label: criterionLabelMap[s.criterion_key] || s.criterion_key,
            score: s.score,
            level,
            occurrence: count
          });
        }
      }
      if (flags.length > 0) {
        result[ev.evaluation_id] = flags;
      }
    }
    return result;
  }, [allEvaluations, myEvaluations, givenEvaluations, criterionLabelMap]);

  const flagColorClasses = {
    yellow: 'bg-yellow-100 text-yellow-800 border-yellow-300',
    orange: 'bg-orange-100 text-orange-800 border-orange-300',
    red: 'bg-red-100 text-red-800 border-red-300'
  };
  const flagIconColor = {
    yellow: 'text-yellow-600',
    orange: 'text-orange-600',
    red: 'text-red-600'
  };

  const EvaluationCard = ({ evaluation }) => {
    const event = events[evaluation.event_id];
    const evaluator = users[evaluation.evaluator_id];
    const evaluatee = users[evaluation.evaluatee_id];
    const flags = flagsByEvalId[evaluation.evaluation_id];
    
    return (
      <Card 
        className="border-0 shadow-md hover:shadow-lg transition-shadow cursor-pointer"
        onClick={() => {/* View details */}}
        data-testid={`evaluation-card-${evaluation.evaluation_id}`}
      >
        <CardContent className="p-5">
          <div className="flex items-start justify-between mb-3">
            <Badge variant="outline" className="text-xs">
              {getEvaluationTypeLabel(evaluation.evaluation_type)}
            </Badge>
            <StatusBadge status={evaluation.status} size="small" showLabel={false} />
          </div>
          
          <div className="space-y-3">
            {event && (
              <div className="flex items-center gap-2 text-sm">
                <Calendar size={14} className="text-gray-400" />
                <span className="text-gray-600">{event.name}</span>
              </div>
            )}
            
            <div className="flex items-center gap-2 text-sm">
              <User size={14} className="text-gray-400" />
              <span className="text-gray-600">
                {activeTab === 'received' 
                  ? `${language === 'el' ? 'Από' : 'From'}: ${evaluator?.name || 'Unknown'}`
                  : activeTab === 'given'
                  ? `${language === 'el' ? 'Προς' : 'To'}: ${evaluatee?.name || 'Unknown'}`
                  : `${evaluator?.name || '?'} → ${evaluatee?.name || '?'}`
                }
              </span>
            </div>
            
            <div className="pt-2">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-gray-500">{t('averageScore')}</span>
                <span className="font-bold text-[#2A1514]">{evaluation.average_score.toFixed(1)}/5</span>
              </div>
              <ScoreBar score={evaluation.average_score} showValue={false} />
            </div>

            {flags && flags.length > 0 && (
              <div
                className="pt-2 border-t border-dashed border-gray-200"
                data-testid={`evaluation-flags-${evaluation.evaluation_id}`}
              >
                <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 mb-1.5">
                  {language === 'el' ? 'Σημαίες ανάπτυξης' : 'Development flags'}
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {flags.map((f, i) => (
                    <div
                      key={i}
                      className={`flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] ${flagColorClasses[f.level]}`}
                      title={`${f.label} — ${language === 'el' ? 'Βαθμός' : 'Score'} ${f.score}/5 · ${language === 'el' ? 'Επανάληψη' : 'Occurrence'} #${f.occurrence}`}
                      data-testid={`flag-${f.level}-${f.key}`}
                    >
                      <Flag size={10} className={flagIconColor[f.level]} fill="currentColor" />
                      <span className="font-medium">{f.label}</span>
                      <span className="opacity-70">×{f.occurrence}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            <div className="flex items-center justify-between pt-2 text-xs text-gray-400">
              <span>
                {format(new Date(evaluation.created_at), 'PPP', { locale: language === 'el' ? el : enUS })}
              </span>
              <div className="flex items-center gap-1">
                {isAdmin && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-red-400 hover:text-red-600 hover:bg-red-50"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (window.confirm(language === 'el' ? 'Διαγραφή αξιολόγησης;' : 'Delete evaluation?')) {
                        evaluationsApi.delete(evaluation.evaluation_id).then(() => {
                          toast.success(language === 'el' ? 'Αξιολόγηση διαγράφηκε!' : 'Evaluation deleted!');
                          loadData();
                        }).catch(() => toast.error(t('error')));
                      }
                    }}
                    title={language === 'el' ? 'Διαγραφή' : 'Delete'}
                    data-testid={`delete-eval-${evaluation.evaluation_id}`}
                  >
                    <Trash2 size={14} />
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-[#E6007E] hover:bg-[#E6007E]/10"
                  onClick={(e) => { e.stopPropagation(); evaluationsApi.exportPdf(evaluation.evaluation_id); }}
                  title={language === 'el' ? 'Λήψη PDF' : 'Download PDF'}
                  data-testid={`export-pdf-${evaluation.evaluation_id}`}
                >
                  <FileDown size={14} />
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in-up overflow-x-hidden" data-testid="evaluations-page">
        {/* Header */}
        <div className="flex flex-col gap-4">
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold text-[#2A1514]">{t('evaluations')}</h1>
            <p className="text-gray-500 mt-1 text-sm">
              {language === 'el' 
                ? 'Προβολη και διαχειριση αξιολογησεων'
                : 'View and manage evaluations'
              }
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger className="w-[140px] sm:w-[160px]" data-testid="period-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {timePeriods.map(p => (
                  <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {!isAdmin && (
              <Button 
                onClick={() => navigate('/evaluations/new')}
                className="bg-[#E6007E] hover:bg-[#b8005f] text-sm"
                data-testid="new-evaluation-btn"
              >
                <Plus size={16} className="mr-1.5" />
                {t('newEvaluation')}
              </Button>
            )}
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          {!isAdmin && (
            <TabsList className="grid w-full max-w-lg grid-cols-2">
              <TabsTrigger value="received" data-testid="tab-received">
                {language === 'el' ? 'Αξιολογήσεις μου' : 'My Evaluations'}
              </TabsTrigger>
              <TabsTrigger value="given" data-testid="tab-given">
                {language === 'el' ? 'Αξιολογήσεις που έδωσα' : 'Evaluations I Gave'}
              </TabsTrigger>
            </TabsList>
          )}
          
          <TabsContent value="received" className="mt-6">
            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {[1,2,3].map(i => (
                  <Card key={i} className="border-0 shadow-md animate-pulse">
                    <CardContent className="p-5">
                      <div className="h-4 bg-gray-200 rounded w-1/3 mb-4" />
                      <div className="h-3 bg-gray-200 rounded w-2/3 mb-2" />
                      <div className="h-3 bg-gray-200 rounded w-1/2" />
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : myEvaluations.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 stagger-children">
                {myEvaluations.map(eval_item => (
                  <EvaluationCard key={eval_item.evaluation_id} evaluation={eval_item} />
                ))}
              </div>
            ) : (
              <Card className="border-0 shadow-md">
                <CardContent className="p-12 text-center">
                  <ClipboardCheck size={48} className="mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-500">{t('noEvaluations')}</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>
          
          <TabsContent value="given" className="mt-6">
            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {[1,2,3].map(i => (
                  <Card key={i} className="border-0 shadow-md animate-pulse">
                    <CardContent className="p-5">
                      <div className="h-4 bg-gray-200 rounded w-1/3 mb-4" />
                      <div className="h-3 bg-gray-200 rounded w-2/3 mb-2" />
                      <div className="h-3 bg-gray-200 rounded w-1/2" />
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : givenEvaluations.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 stagger-children">
                {givenEvaluations.map(eval_item => (
                  <EvaluationCard key={eval_item.evaluation_id} evaluation={eval_item} />
                ))}
              </div>
            ) : (
              <Card className="border-0 shadow-md">
                <CardContent className="p-12 text-center">
                  <ClipboardCheck size={48} className="mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-500">{t('noEvaluations')}</p>
                  <Button 
                    className="mt-4 bg-[#E6007E] hover:bg-[#b8005f]"
                    onClick={() => navigate('/evaluations/new')}
                  >
                    <Plus size={18} className="mr-2" />
                    {t('newEvaluation')}
                  </Button>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {isAdmin && (
            <TabsContent value="all" className="mt-6 space-y-4">
              {/* Search by name */}
              <div className="relative max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                <Input
                  placeholder={language === 'el' ? 'Αναζήτηση με όνομα...' : 'Search by name...'}
                  value={searchName}
                  onChange={(e) => setSearchName(e.target.value)}
                  className="pl-9 h-9"
                  data-testid="search-name-input"
                />
              </div>
              {loading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {[1,2,3].map(i => (
                    <Card key={i} className="border-0 shadow-md animate-pulse">
                      <CardContent className="p-5">
                        <div className="h-4 bg-gray-200 rounded w-1/3 mb-4" />
                        <div className="h-3 bg-gray-200 rounded w-2/3 mb-2" />
                        <div className="h-3 bg-gray-200 rounded w-1/2" />
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (() => {
                const filtered = allEvaluations.filter(e => {
                  if (!searchName.trim()) return true;
                  const term = searchName.toLowerCase();
                  const evaluator = users[e.evaluator_id];
                  const evaluatee = users[e.evaluatee_id];
                  return (evaluator?.name || '').toLowerCase().includes(term) ||
                         (evaluatee?.name || '').toLowerCase().includes(term);
                });
                return filtered.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 stagger-children">
                    {filtered.map(eval_item => (
                      <EvaluationCard key={eval_item.evaluation_id} evaluation={eval_item} />
                    ))}
                  </div>
                ) : (
                  <Card className="border-0 shadow-md">
                    <CardContent className="p-12 text-center">
                      <ClipboardCheck size={48} className="mx-auto text-gray-300 mb-4" />
                      <p className="text-gray-500">{searchName ? (language === 'el' ? 'Δεν βρέθηκαν αποτελέσματα' : 'No results found') : t('noEvaluations')}</p>
                    </CardContent>
                  </Card>
                );
              })()}
            </TabsContent>
          )}
        </Tabs>
      </div>
    </Layout>
  );
}
